import { DocumentType, Work } from "@biblioteksentralen/cordata";
import { Stack } from "@biblioteksentralen/react";
import { ComponentProps, MutableRefObject } from "react";
import { DefaultContainer } from "../../../../components/ContentContainer";
import { isMovie } from "../../../cordata/workTypes";
import { LibraryInfo } from "../../../sanityQueries";
import { SortableManifestation } from "../../../types";
import { CordataDevInfo } from "../../CordataDevInfo";
import { WorkCoverImage } from "../../WorkCoverImage";
import { MovieReservation } from "../../reservation/MovieReservation";
import { Reservation } from "../../reservation/Reservation";
import { reservationFullWidthBreakpoint } from "../../reservation/ReservationContainer";
import { ReservationTextContainer } from "../../reservation/ReservationTextContainer";
import { WorkInfo } from "./WorkInfo";
import { CoverImageQuality } from "@libry-content/integrations";

type WorkViewBannerProps = {
  work: Work;
  representativeManifestation?: SortableManifestation;
  coverImageQuality?: CoverImageQuality;
  libraries: LibraryInfo[];
  documentType: DocumentType | undefined;
  hasWorkHoldings: boolean;
  hasHoldingsDataError?: boolean;
  manifestationsTitleRef: MutableRefObject<HTMLAnchorElement | null>;
};

const reservationLayoutProps = {
  flexGrow: "1",
  minWidth: { base: "100%", [reservationFullWidthBreakpoint]: "16rem" },
  borderRadius: { base: "0", [reservationFullWidthBreakpoint]: "md" },
  height: "100%",
} satisfies Partial<ComponentProps<typeof Reservation>>;

export const WorkViewBanner = ({
  work,
  representativeManifestation,
  coverImageQuality,
  libraries,
  documentType,
  hasWorkHoldings,
  hasHoldingsDataError,
  manifestationsTitleRef,
}: WorkViewBannerProps) => (
  <DefaultContainer withoutPaddingXBelow="lg">
    <Stack
      flexDirection={{ base: "column", [reservationFullWidthBreakpoint]: "row" }}
      alignItems="flex-start"
      justifyContent="flex-start"
      width="100%"
    >
      <Stack
        flexDirection={{ base: "column", sm: "row" }}
        alignItems={{ base: "center", sm: "flex-start" }}
        justifyContent="flex-start"
        flexGrow="1"
        position="relative"
        width="100%"
        spacing="2rem"
        padding={{ base: "1rem", sm: "1.5rem", lg: 0 }}
      >
        <CordataDevInfo data={work} title="verk" position="absolute" top="0.5rem" left="2.5rem" zIndex="2" />
        <WorkCoverImage
          work={work}
          representativeManifestation={representativeManifestation}
          quality={coverImageQuality}
          width={{ base: "8rem", md: "10rem", lg: "12rem" }}
          flexShrink={0}
          showFavoriteButton
        />
        <WorkInfo
          work={work}
          libraries={libraries}
          hasWorkHoldings={hasWorkHoldings}
          manifestationsTitleRef={manifestationsTitleRef}
          flexGrow="1"
          alignSelf="stretch"
        />
      </Stack>
      {hasHoldingsDataError && (
        <ReservationTextContainer
          getText={(t) => t("Noe gikk galt ved henting av beholdningsstatus.")}
          currentDocumentType={documentType}
          {...reservationLayoutProps}
        />
      )}
      {!hasHoldingsDataError && !hasWorkHoldings && (
        <ReservationTextContainer
          getText={(t, name) => `${name} ${t("er for tiden ikke tilgjengelig")}.`}
          currentDocumentType={documentType}
          {...reservationLayoutProps}
        />
      )}
      {!hasHoldingsDataError && hasWorkHoldings && isMovie(work) && (
        <MovieReservation
          work={work}
          libraries={libraries}
          hasWorkHoldings={hasWorkHoldings}
          {...reservationLayoutProps}
        />
      )}
      {!hasHoldingsDataError && hasWorkHoldings && !isMovie(work) && (
        <Reservation work={work} libraries={libraries} {...reservationLayoutProps} />
      )}
    </Stack>
  </DefaultContainer>
);
